import { ChannelOptions, Channel } from './Channel';
import { isInboundChannelMessage, type OutboundMessage, type ConnectionError, type InboundMessage } from './protocol';

import { setConnectionId } from './connectionRef';
import { ConnectionState } from './types';

export interface RealtimeClientOptions {
  onError?: (error: ConnectionError) => void;
  onDisconnect?: () => void;
  // Called when the connection token has expired (or is within `tokenSkew` of it) and we
  // would otherwise reconnect with a stale token. The consumer should mint a fresh session
  // (which yields a new `connect()` with a fresh token). Return the in-flight promise so the
  // client can await it and avoid firing repeated refreshes during reconnect backoff. The
  // `signal` aborts if a new connection flow or teardown supersedes this refresh, so the
  // consumer can cancel the in-flight request.
  onTokenStale?: (signal: AbortSignal) => void | Promise<void>;
  getChannelToken: (channel: string) => Promise<string>;
  heartbeatTimeout?: number; // ms; force reconnect if no frame arrives within this window
  tokenSkew?: number; // ms; treat the connection token as expired this long before its real expiry
}

export interface ConnectOptions {
  endpoint: string;
  token: string;
  ttl: number; // connection token lifetime in seconds
}

export class RealtimeClient {
  private ws: WebSocket | null = null;
  private intentionalClose = false;
  private retryCount = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private endpoint?: string;
  private token?: string;
  // Wall-clock ms when the current connection token expires. Set whenever a fresh token is
  // adopted via connect(); reconnects reuse the same token and leave this untouched.
  private tokenExpiresAt?: number;
  private readonly tokenSkew: number;
  // Identifies the current connection generation. Aborted whenever a new connection flow
  // starts (connect) or the client is torn down (_disconnect), so an in-flight token refresh
  // knows it has been superseded and bows out instead of scheduling a duplicate reconnect chain.
  private generation = new AbortController();

  // Force a reconnect if no frame arrives within this window (half-open socket detection).
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly heartbeatTimeout: number;

  // Reconnecting while the page is hidden or offline produces doomed connections: a
  // sleeping machine authenticates during the sleep transition / dark wakes, then never
  // answers a ping and gets reaped by the server heartbeat. Skip those reconnects and
  // remember we owe one; handleWake performs it when the page is visible and online.
  private reconnectOnWake = false;

  // StrictMode hack
  private disconnectTimer: ReturnType<typeof setTimeout> | null = null;

  public _state: ConnectionState = { status: 'idle' };
  private channels = new Map<string, Channel>();

  private refCounter: number = 0;
  // We queue `subscribe` messages if the
  // token fetch finishes before the WS `connected` handshake finishes!
  private queue: any[] = [];

  constructor(public options: RealtimeClientOptions) {
    this.options = options;
    this.heartbeatTimeout = options.heartbeatTimeout ?? 60000;
    this.tokenSkew = options.tokenSkew ?? 5000;
  }

  private handleWake = (): void => {
    if (!this.reconnectOnWake || document.hidden || !navigator.onLine) return;
    this.reconnectOnWake = false;
    this.retryCount = 0;
    void this.reconnectNow();
  };

  private get isTokenStale(): boolean {
    return this.tokenExpiresAt !== undefined && Date.now() >= this.tokenExpiresAt - this.tokenSkew;
  }

  private newGeneration(): void {
    // A reconnect owed from before is superseded; the fresh connect() or teardown owns it now.
    this.reconnectOnWake = false;
    this.generation.abort();
    this.generation = new AbortController();
  }

  private get isActive(): boolean {
    const s = this._state.status;
    return s === 'connecting' || s === 'connected' || s === 'reconnecting';
  }

  connect(options: ConnectOptions) {
    if (this.disconnectTimer !== null) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }

    if (this.isActive && this.endpoint === options.endpoint && this.token === options.token) {
      return;
    }

    if (this.isActive) {
      this._disconnect();
    }

    this.tokenExpiresAt = Date.now() + options.ttl * 1000;
    this.newGeneration();
    this.intentionalClose = false;
    // Idempotent: duplicate (type, handler) registrations are discarded.
    document.addEventListener('visibilitychange', this.handleWake);
    window.addEventListener('online', this.handleWake);
    this._connect(options);
  }

  private _connect(options: { endpoint: string; token: string }) {
    this.endpoint = options.endpoint;
    this.token = options.token;

    if (this._state.status !== 'reconnecting') {
      this.state = { status: 'connecting' };
    }

    this.ws = new WebSocket(this.endpoint);

    this.ws.onopen = () => {
      console.log('[Realtime] WebSocket opened, sending connect command');
      this.refCounter += 1;

      this.rawSend({
        type: 'connect',
        ref: this.refCounter,
        payload: { jwt: this.token! },
      });

      // Start the watchdog now so a hung handshake also recovers.
      this.resetHeartbeat();
    };

    this.ws.onmessage = (event) => {
      this.resetHeartbeat();
      this.handleMessage(event.data);
    };

    this.ws.onclose = (event) => {
      this.clearHeartbeat();
      console.log('[Realtime] WebSocket closed', { code: event.code });
      this.ws = null;
      this.state = { status: 'disconnected' };

      for (const ch of this.channels.values()) {
        ch.handleDisconnect();
      }

      this.options?.onDisconnect?.();

      if (this.intentionalClose) return;

      if (event.code === 4001) {
        console.error('[Realtime] Connect Token is invalid or expired. Halting reconnects.');
        this.state = { status: 'disconnected' };

        return;
      }
      this.state = { status: 'reconnecting' };
      this.scheduleReconnect();
    };
  }

  send(message: OutboundMessage) {
    if (this.status === 'connected') {
      message.ref = message.ref || ++this.refCounter;
      this.rawSend(message);
    } else {
      this.queue.push(message);
    }
  }

  private rawSend(message: OutboundMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private handleMessage(data: string) {
    let msg: InboundMessage;
    try {
      msg = JSON.parse(data);
    } catch {
      console.error('[Realtime] Failed to parse inbound message', data);
      return;
    }
    if (isInboundChannelMessage(msg)) {
      const ch = this.channels.get(msg.payload.channel);
      if (ch) {
        ch.handleMessage(msg);
      }
      return;
    }

    switch (msg.type) {
      case 'ping':
        this.rawSend({ type: 'pong', payload: { timestamp: msg.payload.timestamp } });
        break;

      case 'connected':
        console.log(`[Realtime] connected user_id=${msg.payload.user_id}, connection_id=${msg.payload.connection_id}`);
        this.retryCount = 0;
        this.state = { status: 'connected', user: msg.payload };
        // Flush the queue
        this.queue.forEach((m) => this.rawSend(m));
        this.queue = [];
        // Resubscribe dropped channels
        this.channels.forEach((ch) => ch.resubscribe());
        break;
      case 'error':
        this.options.onError?.(msg as ConnectionError);
        break;
    }
  }

  private resetHeartbeat(): void {
    if (this.heartbeatTimer) clearTimeout(this.heartbeatTimer);
    this.heartbeatTimer = setTimeout(() => {
      this.heartbeatTimer = null;
      console.warn(`[Realtime] No frame within ${this.heartbeatTimeout}ms; closing socket to trigger reconnect`);
      this.ws?.close(); // → onclose → scheduleReconnect (intentionalClose stays false)
    }, this.heartbeatTimeout);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private _disconnect(): void {
    this.intentionalClose = true;
    this.clearHeartbeat();
    document.removeEventListener('visibilitychange', this.handleWake);
    window.removeEventListener('online', this.handleWake);
    if (this.retryTimer != null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }

    // Keep the channel registry: the Channel objects are held by components
    // via a stable memo, so clearing here would orphan them and resubscribe()
    // would have nothing to iterate after the next handshake.
    for (const ch of this.channels.values()) {
      ch.handleDisconnect();
    }
    this.queue = [];
    this.tokenExpiresAt = undefined;
    this.newGeneration();
    this.state = { status: 'idle' };
  }

  disconnect() {
    // Prevent queueing multiple disconnects
    if (this.disconnectTimer !== null) return;

    // Delay the actual teardown to outlive React's Strict Mode unmount/remount
    this.disconnectTimer = setTimeout(() => {
      this.disconnectTimer = null;
      this._disconnect();
    });
  }

  channel(name: string, options: ChannelOptions): Channel {
    if (!this.channels.has(name)) {
      this.channels.set(name, new Channel(name, this, options));
    }
    return this.channels.get(name)!;
  }

  private get status(): ConnectionState['status'] {
    return this._state.status;
  }

  private get state() {
    return this._state;
  }

  private set state(state: ConnectionState) {
    this._state = state;
    if (state.status === 'connected') {
      setConnectionId(state.user.connection_id);
    } else {
      setConnectionId(null);
    }
    this._notifySubscribers();
  }

  private scheduleReconnect(): void {
    if (document.hidden || !navigator.onLine) {
      console.log('[Realtime] Page hidden or offline; will reconnect on wake');
      this.reconnectOnWake = true;
      return;
    }
    this.retryCount++;
    const delay = [10, 200, 500, 1000, 2000, 5000, 30000][this.retryCount - 1] || 60000;

    console.log(`[Realtime] Reconnecting in ${delay} (attempt ${this.retryCount})`);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.reconnectNow();
    }, delay);
  }

  private async reconnectNow(): Promise<void> {
    if (!this.endpoint || !this.token) return;

    // The retry timer can fire overdue after the tab was hidden/frozen mid-backoff;
    // re-check at fire time, not just at scheduling time.
    if (document.hidden || !navigator.onLine) {
      this.reconnectOnWake = true;
      return;
    }

    // Don't waste a reconnect on a token the server will reject (→ 4001 → halt). Ask the
    // consumer for a fresh session token and await it so we don't fire repeated refreshes
    // while the request is in flight (the early backoff steps are far shorter than a round trip).
    if (this.isTokenStale) {
      console.warn('[Realtime] Connection token stale; requesting refresh instead of reconnecting');
      const { signal } = this.generation;
      await this.options.onTokenStale?.(signal);
      // A fresh token (connect) or teardown (_disconnect) took over while we awaited; that flow
      // owns the reconnect now, so bail to avoid a duplicate chain.
      if (signal.aborted) return;
      // Refresh produced no new token (e.g. session fetch failed); back off and try again.
      this.scheduleReconnect();
      return;
    }

    this._connect({ endpoint: this.endpoint, token: this.token });
  }

  // External Store subscribers (used by React)
  private subscribers = new Set<() => void>();

  subscribe = (callback: () => void): (() => void) => {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  };

  getSnapshot = (): ConnectionState => {
    return this._state;
  };

  private _notifySubscribers() {
    this.subscribers.forEach((cb) => cb());
  }
}
